/**
 * Notes:
 * Image editing is a little more involved then simply updating a field. When updating a field like the
 * company name the result of the edit is the exact same type as what was received from the server (i.e.
 * a string). When editing an image what we are doing is creating local blobs that we can point to via a
 * URL. However this URL is not something we could save to the server because it relies on the existence of
 * the local blob. So when we save we have to take the extra step of saving the image to a remote location and
 * then using this remote URL. This means we need an intermediate step that of saving the image blob references
 * as state.
 */

import { cardContext } from '@context/card-context';
import { CardHeaderEditContext } from '@context/card-header-edit/provider';
import { cardPresetContext } from '@context/card-preset-context';
import { faCube, faPencil, faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { SettableNode } from '@hooks/use-node';
import useRemoteImageBlob from '@hooks/use-remote-image-blob';
import { motion } from 'motion/react';
import { useCallback, useContext, useMemo, useState } from 'react';
import type { CardFields, CardImage, CardLayoutFields, CardPicturesFields } from 'types';
import { CardLayout } from 'types';

import styles from './CardHeader.module.scss';
import PicturePreventOverrideIcon from './CardHeaderPictures/PicturePreventOverrideIcon';
import { EditPicturePopover } from './EditPicturePopover';
import NextImagePopover from './NextImagePopover';
import ProfilePictureDisabledPopover from './ProfilePictureDisabledPopover';
import SelectImageTypePopover from './SelectImageTypePopover';
import {
  type ImageType,
  getSuggestedLayout,
  hasLayouts,
  imageLocationsFromLayout,
  isImageLinked,
  typeToFieldId,
} from './utilities';

type CardMode = 'fit' | 'standalone';

export type CardHeaderProps = CardPicturesFields &
  CardLayoutFields & {
    node?: SettableNode<HTMLHeadingElement> | undefined;
    editMode: boolean;
    createMode: boolean;
    mode?: CardMode;
    isPreset: boolean;
    picturePreview?: ImageType | undefined;
  };

function calcIsLocked(
  type: ImageType | undefined,
  lockedFields: string[],
  layoutImagesLocked: boolean,
): boolean {
  switch (type) {
    case 'cover': {
      return lockedFields.includes(typeToFieldId('cover')) || layoutImagesLocked;
    }
    case 'profile': {
      return (
        lockedFields.includes(typeToFieldId('profile')) ||
        lockedFields.includes('profile') || // backwards compat
        layoutImagesLocked
      );
    }
    case 'logo': {
      return (
        lockedFields.includes(typeToFieldId('logo')) ||
        lockedFields.includes('banner') || // backwards compat
        layoutImagesLocked
      );
    }
    default:
      return false;
  }
}

// The pencil button and overlay that shows when hovering over a card.
const EditCardImageButton = ({
  onClick,
  location,
}: {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  location: ImagePosition;
}) => {
  return (
    <button className={styles['edit-image-overlay-btn']} data-location={location} onClick={onClick}>
      <FontAwesomeIcon icon={faPencil} className={styles['overlay-edit-image-icon']} />
    </button>
  );
};

/**  re-usable interface for blob information
 */
interface ImgBlob {
  blob: Blob | undefined;
  imageUrl: string | undefined;
  updateImageUrl: React.Dispatch<React.SetStateAction<string | undefined>>;
  resetImageUrl: () => void;
  error: boolean;
}
/** Blob information for all image types */
export interface ImgBlobs {
  profile: ImgBlob;
  logo: ImgBlob;
  cover: ImgBlob;
}
/**
 *
 * @param editMode Is the header in edit mode
 * @param fields Card image picture fields
 * @param previewUrls Any preview URLs of the card images. Used in edit mode only.
 * @param previews Implies the user wants to see where the iamge might go, evenif that layout isn't selected explicitly.
 * @param originalImgBlobs Image blobs of the original images (not the edited imaged)
 * @param layout The card header layout
 * @returns CardImage for each image, by location and by name
 */
export const getImgUrls = (
  editMode: boolean,
  fields: CardPicturesFields,
  previewUrls: {
    profilePicturePreviewUrl?: string;
    logoImagePreviewUrl?: string;
    coverPhotoPreviewUrl?: string;
  },
  previews: {
    left: boolean;
    right: boolean;
  },
  originalImgBlobs: ImgBlobs,
  layout: CardLayout,
): {
  left?: CardImage;
  right?: CardImage;
  banner?: CardImage;
  logo?: CardImage;
  profile?: CardImage;
  cover?: CardImage;
} => {
  const images = {
    // {...} prevent this from overwriting data in the parents card object
    profile: { ...fields.profilePictureImg },
    logo: { ...fields.companyLogoImg },
    cover: { ...fields.coverPhotoImg },
  };
  if (editMode) {
    images.profile.editedImageUrl =
      previewUrls.profilePicturePreviewUrl ||
      images.profile.editedImageUrl ||
      originalImgBlobs.profile.imageUrl;
    images.profile.editorMetadata ??= {};
    images.profile.editorMetadata.editorVersion ??= 'web_editor_v1';
    images.logo.editedImageUrl = previewUrls.logoImagePreviewUrl || images.logo.editedImageUrl;
    images.cover.editedImageUrl = previewUrls.coverPhotoPreviewUrl || images.cover.editedImageUrl;
  }

  const locations = imageLocationsFromLayout(layout, previews);
  const imagesWithLocations = {
    banner: locations.banner ? images[locations.banner] : undefined,
    left: locations.left ? images[locations.left] : undefined,
    right: locations.right ? images[locations.right] : undefined,
    ...images,
  };
  return imagesWithLocations;
};
type ImagePosition = 'left' | 'right' | 'banner';
interface ShowPopover {
  position: ImagePosition;
  type?: ImageType;
}
/** Calculate the image type from the popover information */
const defaultTypeFromPopover = (p?: ShowPopover, layout?: CardLayout): ImageType => {
  if (!p) {
    return 'cover';
  }
  if (p.type) {
    return p.type;
  }
  const locations: { left: ImageType; right: ImageType; banner: ImageType } = {
    left: 'profile',
    right: 'logo',
    banner: 'cover',
  };
  if (layout) {
    const locationsFromLayout = imageLocationsFromLayout(layout);
    locations.banner = locationsFromLayout.banner || locations.banner;
    locations.left = locationsFromLayout.left || locations.left;
    locations.right = locationsFromLayout.right || locations.right;
  }

  return locations[p.position];
};

const CardHeader: React.FC<React.PropsWithChildren<CardHeaderProps>> = ({
  profilePictureImg,
  companyLogoImg,
  coverPhotoImg,
  createMode,
  editMode,
  isPreset,
  layout, // we should pass in layout here, because we want to be able to override it for previews
  mode = 'standalone',
  node,
}) => {
  const [showPopover, setShowPopover] = useState<ShowPopover>();
  const [askForNextImage, setAskForNextImage] = useState<boolean | undefined>();
  const [showSelectImageTypePopover, setShowSelectImageTypePopover] = useState<boolean | undefined>();
  const [showCantAddProfilePopover, setShowCantAddProfilePopover] = useState<boolean | undefined>();
  const context_preset = useContext(cardPresetContext);
  const context_card = useContext(cardContext);
  // switch the context source based on whether this is a preset header
  const context = isPreset ? context_preset : context_card;
  const cardId = context_card.cardId;
  const presetId = context_preset.presetId;
  const userId = context_card.userId;
  const orgId = context_preset.orgId;
  const interactive = context_card.interactive ?? true;

  const [editState, editDispatch] = useContext(CardHeaderEditContext);
  const {
    coverPhotoPreviewUrl,
    coverQuality,
    logoImagePreviewUrl,
    logoQuality,
    picturePreview,
    profilePicturePreviewUrl,
    profileQuality,
    appliedPresetId,
  } = editState;

  const setLogoImagePreviewUrl = useCallback(
    (value: string, quality: 'high' | 'low' | undefined) => {
      editDispatch({ type: 'SET_LOGO_IMAGE_PREVIEW_URL', value, quality });
    },
    [editDispatch],
  );
  const setCoverPhotoPreviewUrl = useCallback(
    (value: string, quality: 'high' | 'low' | undefined) => {
      editDispatch({ type: 'SET_COVER_PHOTO_PREVIEW_URL', value, quality });
    },
    [editDispatch],
  );
  const setProfilePicturePreviewUrl = useCallback(
    (value: string, quality: 'high' | 'low' | undefined) => {
      editDispatch({ type: 'SET_PROFILE_PICTURE_PREVIEW_URL', value, quality });
    },
    [editDispatch],
  );

  const { canLock, cardFields, cardFieldsDispatch, lockedFields, layoutImagesLocked, toggleFieldLock } =
    context;

  // We should download the original images as blobs, so they can be manipulated in a canvas
  const profileBlob = useRemoteImageBlob(profilePictureImg?.originalImageUrl);
  const logoBlob = useRemoteImageBlob(companyLogoImg?.originalImageUrl);
  const coverBlob = useRemoteImageBlob(coverPhotoImg?.originalImageUrl);
  const imgBlobs = useMemo(() => {
    return {
      profile: profileBlob,
      logo: logoBlob,
      cover: coverBlob,
    };
  }, [profileBlob, logoBlob, coverBlob]);

  // compute the layout we should have, based on incoming layout and which image has been clicked
  const computedLayout = useMemo(() => {
    if (layout) return layout;
    if (showPopover?.position === 'left') {
      return CardLayout.Layout_2C;
    }
    if (showPopover?.position === 'banner') {
      return CardLayout.Layout_1B;
    }
    return CardLayout.Layout_1B;
  }, [layout, showPopover?.position]);

  const imageLocations = useMemo(() => {
    const locations = imageLocationsFromLayout(computedLayout ?? CardLayout.Layout_1B); //default to logo in banner
    return locations;
  }, [computedLayout]);

  // get the quality of each picture. Quality is low on fast renders from the editor.
  const { rightQuality, leftQuality, bannerQuality } = useMemo(() => {
    return {
      bannerQuality:
        imageLocations.banner === 'cover'
          ? coverQuality
          : imageLocations.banner === 'logo'
            ? logoQuality
            : imageLocations.banner === 'profile'
              ? profileQuality
              : undefined,
      rightQuality:
        imageLocations.right === 'cover'
          ? coverQuality
          : imageLocations.right === 'logo'
            ? logoQuality
            : imageLocations.right === 'profile'
              ? profileQuality
              : undefined,
      leftQuality:
        imageLocations.left === 'cover'
          ? coverQuality
          : imageLocations.left === 'logo'
            ? logoQuality
            : imageLocations.left === 'profile'
              ? profileQuality
              : undefined,
    };
  }, [imageLocations, coverQuality, logoQuality, profileQuality]);

  const handleEditPictureClose = useCallback(() => {
    imgBlobs.profile.resetImageUrl();
    imgBlobs.cover.resetImageUrl();
    imgBlobs.logo.resetImageUrl();
    setProfilePicturePreviewUrl('', undefined);
    setLogoImagePreviewUrl('', undefined);
    setCoverPhotoPreviewUrl('', undefined);
    setShowPopover(undefined);
    editDispatch({ type: 'SET_QUALITY' });
    editDispatch({ type: 'SET_PICTURE_PREVIEW', value: undefined });
  }, [imgBlobs, setProfilePicturePreviewUrl, setLogoImagePreviewUrl, setCoverPhotoPreviewUrl, editDispatch]);

  const onRemovePicture = useCallback(() => {
    const imageType = defaultTypeFromPopover(showPopover, computedLayout);
    if (imageType === 'profile') {
      const layout = getSuggestedLayout({
        cover: coverPhotoImg?.editedImageUrl,
        profile: undefined,
        logo: companyLogoImg?.editedImageUrl,
      });
      cardFieldsDispatch({
        type: 'REMOVE_PROFILE_PICTURE',
        layout,
      });
      // type workaround because UNLINK_ATTRIBUTE isn't available on cardPresetDispatch
      isPreset &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (cardFieldsDispatch as any)({ type: 'UNLINK_ATTRIBUTE', id: 'profile_picture_img' });

      imgBlobs.profile.updateImageUrl(undefined);
      setProfilePicturePreviewUrl('', undefined);

      if (lockedFields.includes('profile')) {
        toggleFieldLock('profile', false); // remove profile from the list (backwards compat)
      }
      if (lockedFields.includes(typeToFieldId('profile'))) {
        toggleFieldLock(typeToFieldId('profile'), false); // remove profile from the list
      }
    } else if (imageType === 'logo') {
      const layouts = hasLayouts({
        cover: coverPhotoImg?.editedImageUrl,
        profile: profilePictureImg?.editedImageUrl,
        logo: undefined,
      });
      cardFieldsDispatch({
        type: 'REMOVE_COMPANY_LOGO',
        layout: layouts.length > 0 ? layouts[layouts.length - 1] : undefined,
      });
      imgBlobs.logo.updateImageUrl(undefined);
      setLogoImagePreviewUrl('', undefined);

      if (lockedFields.includes('banner')) {
        toggleFieldLock('banner', false); // remove logo (backwards compat)
      }
      if (lockedFields.includes(typeToFieldId('logo'))) {
        toggleFieldLock(typeToFieldId('logo'), false); // remove logo
      }
    } else if (imageType === 'cover') {
      const layouts = hasLayouts({
        cover: undefined,
        profile: profilePictureImg?.editedImageUrl,
        logo: companyLogoImg?.editedImageUrl,
      });
      cardFieldsDispatch({
        type: 'REMOVE_COVER_PHOTO',
        layout: layouts.length > 0 ? layouts[layouts.length - 1] : undefined,
      });
      imgBlobs.cover.updateImageUrl(undefined);
      setCoverPhotoPreviewUrl('', undefined);

      if (lockedFields.includes(typeToFieldId('cover'))) {
        toggleFieldLock(typeToFieldId('cover'), false);
      }
    }
  }, [
    cardFieldsDispatch,
    companyLogoImg,
    coverPhotoImg,
    imgBlobs,
    lockedFields,
    profilePictureImg,
    setCoverPhotoPreviewUrl,
    setLogoImagePreviewUrl,
    setProfilePicturePreviewUrl,
    toggleFieldLock,
    isPreset,
    computedLayout,
    showPopover,
  ]);

  const onSelectImage = useCallback(
    (image: CardImage) => {
      const type = defaultTypeFromPopover(showPopover, computedLayout);
      // update the layout here automatically, so they get the best view of all their pictures.
      if (image.originalImageUrl) {
        if (type === 'profile') {
          imgBlobs.profile.updateImageUrl(image.originalImageUrl);
          setProfilePicturePreviewUrl(image.originalImageUrl, 'low');
          if (image.editorMetadata?.editorVersion) {
            editDispatch({
              type: 'SET_EDITOR_VERSION',
              profile: image.editorMetadata?.editorVersion,
            });
          }
          const layout = getSuggestedLayout({
            cover: coverPhotoImg?.editedImageUrl,
            profile: true,
            logo: companyLogoImg?.editedImageUrl,
          });
          cardFieldsDispatch({
            type: 'SET_PROFILE_PICTURE',
            value: image,
            layout,
          });
        } else if (type === 'logo') {
          imgBlobs.logo.updateImageUrl(image.originalImageUrl);
          setLogoImagePreviewUrl(image.originalImageUrl, 'low');
          if (image.editorMetadata?.editorVersion) {
            editDispatch({
              type: 'SET_EDITOR_VERSION',
              logo: image.editorMetadata?.editorVersion,
            });
          }
          const layout = getSuggestedLayout({
            cover: coverPhotoImg?.editedImageUrl,
            profile: profilePictureImg?.editedImageUrl,
            logo: true,
          });
          cardFieldsDispatch({
            type: 'SET_COMPANY_LOGO',
            value: image,
            layout,
          });
        } else if (type === 'cover') {
          imgBlobs.cover.updateImageUrl(image.originalImageUrl);
          setCoverPhotoPreviewUrl(image.originalImageUrl, 'low');
          if (image.editorMetadata?.editorVersion) {
            editDispatch({
              type: 'SET_EDITOR_VERSION',
              cover: image.editorMetadata?.editorVersion,
            });
          }
          const layout = getSuggestedLayout({
            cover: true,
            profile: profilePictureImg?.editedImageUrl,
            logo: companyLogoImg?.editedImageUrl,
          });
          cardFieldsDispatch({
            type: 'SET_COVER_PHOTO',
            value: image,
            layout,
          });
        }
      }

      if (showPopover) {
        setShowPopover({ type, ...showPopover });
      }
    },
    [
      imgBlobs,
      setCoverPhotoPreviewUrl,
      setLogoImagePreviewUrl,
      setProfilePicturePreviewUrl,
      showPopover,
      cardFieldsDispatch,
      editDispatch,
      computedLayout,
      coverPhotoImg?.editedImageUrl,
      companyLogoImg?.editedImageUrl,
      profilePictureImg?.editedImageUrl,
    ],
  );

  const handleImageUpdate = useCallback(
    (image: CardImage, quality: 'high' | 'low' | undefined) => {
      if (showPopover) {
        const type = defaultTypeFromPopover(showPopover, computedLayout);
        if (!type) {
          editDispatch({ type: 'SET_QUALITY' }); // reset quality
        }
        if (image.editedImageUrl) {
          if (type === 'logo') {
            setLogoImagePreviewUrl(image.editedImageUrl, quality);
            if (image.editorMetadata?.editorVersion) {
              editDispatch({
                type: 'SET_EDITOR_VERSION',
                logo: image.editorMetadata?.editorVersion,
              });
            }
          } else if (type === 'profile') {
            setProfilePicturePreviewUrl(image.editedImageUrl, quality);
            editDispatch({ type: 'SET_QUALITY', profile: quality });
            if (image.editorMetadata?.editorVersion) {
              editDispatch({
                type: 'SET_EDITOR_VERSION',
                profile: image.editorMetadata?.editorVersion,
              });
            }
          } else if (type === 'cover') {
            editDispatch({ type: 'SET_QUALITY', cover: quality });
            setCoverPhotoPreviewUrl(image.editedImageUrl, quality);
            if (image.editorMetadata?.editorVersion) {
              editDispatch({
                type: 'SET_EDITOR_VERSION',
                cover: image.editorMetadata?.editorVersion,
              });
            }
          }
        }
      }
    },
    [
      setLogoImagePreviewUrl,
      setProfilePicturePreviewUrl,
      setCoverPhotoPreviewUrl,
      editDispatch,
      computedLayout,
      showPopover,
    ],
  );

  // calculate and store the images we need in the header.
  const images = useMemo(() => {
    const urls = getImgUrls(
      editMode,
      {
        profilePictureImg,
        companyLogoImg,
        coverPhotoImg,
      },
      {
        profilePicturePreviewUrl,
        coverPhotoPreviewUrl,
        logoImagePreviewUrl,
      },
      {
        left: picturePreview === 'profile',
        right: picturePreview === 'logo',
      },
      imgBlobs,
      computedLayout,
    );

    return urls;
  }, [
    editMode,
    computedLayout,
    imgBlobs,
    profilePictureImg,
    companyLogoImg,
    coverPhotoImg,
    profilePicturePreviewUrl,
    logoImagePreviewUrl,
    coverPhotoPreviewUrl,
    picturePreview,
  ]);

  // this effect shows a popover when a use uploads a photo that's not a profile
  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  const handleCheckAskForNextImage = useCallback(() => {
    if (createMode && askForNextImage === undefined) {
      // potentially open the next image if there's something in the banner
      const imageCount = [
        images.profile?.originalImageUrl,
        images.logo?.originalImageUrl,
        images.cover?.originalImageUrl,
      ].filter(Boolean).length;
      if (
        imageCount === 1 && // only ask when 1 image has been added
        !appliedPresetId && // don't ask when presets applied
        !isPreset && // dont ask for preset
        (imageLocations.banner === 'logo' || imageLocations.banner === 'cover') && // don't ask unless the other is there
        !images.profile?.originalImageUrl && // don't ask if profile exist
        (images.logo?.originalImageUrl || images.cover?.originalImageUrl) && // only ask if another image has been added
        !lockedFields.includes(typeToFieldId('profile')) // don't ask if profile is locked
      ) {
        setAskForNextImage(true);
      } else if (imageCount > 1) {
        setAskForNextImage(false);
      }
    }
  }, [
    askForNextImage,
    createMode,
    imageLocations.banner,
    images.cover?.originalImageUrl,
    images.logo?.originalImageUrl,
    images.profile?.originalImageUrl,
    isPreset,
    lockedFields,
    appliedPresetId,
    setAskForNextImage,
  ]);

  const onPictureSave = useCallback(
    (image: CardImage) => {
      const type = defaultTypeFromPopover(showPopover, computedLayout);
      // we don't update the layout here automatically, because they might just be edited an image.
      switch (type) {
        case 'profile': {
          cardFieldsDispatch({
            type: 'SET_PROFILE_PICTURE',
            value: image,
            layout,
          });
          imgBlobs.profile.resetImageUrl();
          break;
        }
        case 'logo': {
          cardFieldsDispatch({
            type: 'SET_COMPANY_LOGO',
            value: image,
            layout,
          });
          imgBlobs.logo.resetImageUrl();
          break;
        }
        case 'cover': {
          cardFieldsDispatch({
            type: 'SET_COVER_PHOTO',
            value: image,
            layout,
          });
          imgBlobs.cover.resetImageUrl();
          break;
        }
      }

      handleCheckAskForNextImage();
    },
    [cardFieldsDispatch, handleCheckAskForNextImage, imgBlobs, computedLayout, showPopover, layout],
  );

  const imageReferenceForEditing = useMemo(() => {
    const type = defaultTypeFromPopover(showPopover, computedLayout);
    if (showPopover) {
      if (showPopover.position === 'left' && images.left) {
        return images.left;
      }
      if (showPopover.position === 'right' && images.right) {
        return images.right;
      }
      if (showPopover.position === 'banner' && images.banner) {
        return images.banner;
      }
    }
    return images[type] ?? {};
  }, [showPopover, images, computedLayout]);

  const isBannerLocked = useMemo(
    () => calcIsLocked(imageLocations.banner, lockedFields, layoutImagesLocked),
    [imageLocations.banner, lockedFields, layoutImagesLocked],
  );
  const isLeftLocked = useMemo(
    () => calcIsLocked(imageLocations.left, lockedFields, layoutImagesLocked),
    [imageLocations.left, lockedFields, layoutImagesLocked],
  );
  const isRightLocked = useMemo(
    () => calcIsLocked(imageLocations.right, lockedFields, layoutImagesLocked),
    [imageLocations.right, lockedFields, layoutImagesLocked],
  );

  // for the next layout, calculate whether we will preview an image
  const nextLayout = getSuggestedLayout({
    cover: images.cover?.editedImageUrl,
    logo: images.logo?.editedImageUrl ?? picturePreview === 'logo',
    profile: !isPreset && (images.profile?.editedImageUrl ?? picturePreview === 'profile'), // don't preview profile on preset
  });
  const nextLocations = imageLocationsFromLayout(nextLayout ?? CardLayout.Layout_1B);
  const willRightPicturePreview: ImageType | undefined =
    nextLocations.right && picturePreview === nextLocations.right ? picturePreview : undefined;
  const willLeftPicturePreview: ImageType | undefined =
    nextLocations.left && picturePreview === nextLocations.left ? picturePreview : undefined;

  const handleBannerEditClicked = useCallback(() => {
    if (images.banner?.editedImageUrl) {
      setShowPopover({ position: 'banner' });
    } else {
      setShowSelectImageTypePopover(true);
    }
  }, [images.banner?.editedImageUrl]);

  const isPopoverImageLinked = useMemo(
    () => isImageLinked(showPopover?.type, isPreset, (cardFields as CardFields).linkedAttributes),
    [cardFields, isPreset, showPopover?.type],
  );

  return (
    <header
      className={styles['card-header']}
      data-card-layout={computedLayout}
      data-image-type={imageLocations.banner} // which image is in the banner?
      ref={node ? node[1] : undefined}
      data-mode={mode}
      data-edit-mode={editMode}
      data-empty={!images.banner?.editedImageUrl}
      data-floating-left={imageLocations.left ?? willLeftPicturePreview}
      data-has-floating-images={
        !!(imageLocations.left || imageLocations.right) ||
        !!(willRightPicturePreview || willLeftPicturePreview)
      }
    >
      {editMode && (!isBannerLocked || canLock) && (
        <div className={styles['edit-banner-image-overlay']}>
          <EditCardImageButton location="banner" onClick={handleBannerEditClicked} />
        </div>
      )}

      <BannerPicture
        canLock={canLock}
        editMode={editMode}
        editorVersion={editState.logoEditorVersion}
        img={images.banner}
        isBannerLocked={isBannerLocked}
        mode={mode}
        quality={bannerQuality}
        type={imageLocations.banner || 'cover'}
      />

      {(imageLocations.left || willLeftPicturePreview) && (
        <LeftPicture
          alt="Profile"
          canLock={canLock}
          disable={imgBlobs.profile.error}
          editMode={editMode}
          isLocked={isLeftLocked}
          isPreset={isPreset}
          onClick={() => setShowPopover({ position: 'left', ...showPopover })}
          onClickWhenDisabled={() => isPreset && setShowCantAddProfilePopover(true)}
          quality={leftQuality}
          type={imageLocations.left ?? willLeftPicturePreview ?? 'profile'}
          url={images.left?.editedImageUrl}
          interactive={interactive}
        />
      )}
      {(imageLocations.right || willRightPicturePreview) && (
        <RightPicture
          canLock={canLock}
          type={imageLocations.right || 'logo'}
          editMode={editMode}
          isLocked={isRightLocked}
          url={images.right?.editedImageUrl}
          alt="Logo"
          onClick={() => setShowPopover({ position: 'right', ...showPopover })}
          disable={imgBlobs.logo.error}
          quality={rightQuality}
          interactive={interactive}
        />
      )}

      {editMode && (
        <>
          <EditPicturePopover
            isPreset={isPreset}
            cardId={cardId}
            presetId={presetId}
            orgId={orgId}
            userId={userId}
            targetNode={node ? node[0] : undefined}
            show={!!showPopover}
            onClose={handleEditPictureClose}
            onUpdate={handleImageUpdate}
            imageReference={imageReferenceForEditing}
            onRemove={onRemovePicture}
            onSave={onPictureSave}
            onSelect={onSelectImage}
            type={defaultTypeFromPopover(showPopover, computedLayout)}
            canLock={canLock}
            linked={isPopoverImageLinked}
            onUnlink={() => {
              showPopover?.type &&
                isPopoverImageLinked &&
                cardFieldsDispatch({
                  type: 'UNLINK_ATTRIBUTE',
                  id: typeToFieldId(showPopover.type),
                });
            }}
          />

          <SelectImageTypePopover
            isPreset={isPreset}
            onClose={() => setShowSelectImageTypePopover(false)}
            onSelect={(type) => {
              setShowSelectImageTypePopover(false);
              setShowPopover({ position: 'banner', type });
            }}
            show={showSelectImageTypePopover}
            targetNode={node ? node[0] : undefined}
          />
          <NextImagePopover
            onYes={(type) => {
              editDispatch({ type: 'SET_PICTURE_PREVIEW', value: 'profile' });
              setShowPopover({ type, position: 'left' });
              setAskForNextImage(false);
            }}
            show={askForNextImage}
            onClose={() => setAskForNextImage(false)}
            targetNode={node ? node[0] : undefined}
          />
          <ProfilePictureDisabledPopover
            show={showCantAddProfilePopover}
            onClose={() => setShowCantAddProfilePopover(false)}
            targetNode={node ? node[0] : undefined}
          />
        </>
      )}
    </header>
  );
};

interface BannerPictureProps {
  canLock: boolean;
  editMode?: boolean;
  editorVersion: string | undefined;
  img?: CardImage;
  isBannerLocked?: boolean;
  mode: CardMode;
  quality: 'high' | 'low' | undefined;
  type: ImageType;
}
const BannerPicture: React.FC<React.PropsWithChildren<BannerPictureProps>> = ({
  canLock,
  editMode,
  editorVersion,
  img,
  isBannerLocked,
  mode,
  quality,
  type,
}) => {
  const alt = type;
  if (img?.editedImageUrl) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={styles['banner-image-container']}
        data-image-type={type}
        data-is-locked={isBannerLocked}
        data-can-lock={canLock}
        data-edit-mode={editMode}
      >
        <img
          className={styles['banner-image']}
          alt={alt}
          src={img.editedImageUrl}
          data-mode={mode}
          data-image-type={type}
          data-edit-mode={editMode}
          data-editor-version={editorVersion || img.editorMetadata?.editorVersion || 'none'}
          data-img-quality={quality}
        />

        {editMode && <PicturePreventOverrideIcon locked={isBannerLocked} position="top-left" />}
      </motion.div>
    );
  } else {
    return <div className={styles['circle-container']} />;
  }
};
interface PictureProps {
  alt: string;
  canLock: boolean;
  disable?: boolean;
  editMode: boolean;
  isPreset?: boolean;
  isLocked: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  onClickWhenDisabled?: React.MouseEventHandler<HTMLButtonElement>;
  quality: 'high' | 'low' | undefined;
  type: ImageType;
  url: string | undefined;
  interactive?: boolean;
}

/** Left picture is always circular */
const LeftPicture: React.FC<React.PropsWithChildren<PictureProps>> = ({
  alt,
  canLock,
  editMode,
  isLocked,
  isPreset,
  onClick,
  onClickWhenDisabled,
  quality,
  url,
  type,
  interactive = true,
}) => {
  if (!editMode && !url) return null;

  const isClickingDisabled = editMode && (!isLocked || canLock) && type === 'profile' && isPreset;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles['left-picture']}
      data-is-locked={isLocked}
      data-can-lock={canLock}
      data-edit-mode={editMode}
      data-has-image={!!url}
      data-image-type={type}
      data-is-interactive={interactive}
    >
      {url && (
        <>
          <img className={styles['left-picture-img']} alt={alt} src={url} data-img-quality={quality} />
          {isClickingDisabled ? (
            <button
              className={styles['edit-image-overlay-btn']}
              data-location={location}
              onClick={onClickWhenDisabled}
            />
          ) : (
            editMode && (!isLocked || canLock) && <EditCardImageButton location="right" onClick={onClick} />
          )}
        </>
      )}
      {!url && !isPreset && type === 'profile' && (
        // can only edit profile on left if not preset
        <button className={styles['left-picture-edit']} onClick={onClick} disabled={isLocked && !canLock}>
          <FontAwesomeIcon icon={faUser} size="lg" />
        </button>
      )}
      {!url && type === 'logo' && (
        // can edit a logo on the left
        <button className={styles['left-picture-edit']} onClick={onClick} disabled={isLocked && !canLock}>
          <FontAwesomeIcon icon={faCube} size="lg" />
        </button>
      )}
      {editMode && <PicturePreventOverrideIcon locked={isLocked} position="bottom-right" />}
    </motion.div>
  );
};

/** Right picture is always rectangular */
const RightPicture: React.FC<React.PropsWithChildren<PictureProps>> = ({
  editMode,
  url,
  alt,
  canLock,
  isLocked,
  onClick,
  quality,
  interactive = true,
}) => {
  if (!editMode && !url) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles['right-picture']}
      data-is-locked={isLocked}
      data-can-lock={canLock}
      data-edit-mode={editMode}
      data-has-image={!!url}
      data-is-interactive={interactive}
    >
      {url && (
        <>
          <img className={styles['right-picture-img']} alt={alt} src={url} data-img-quality={quality} />
          {editMode && (!isLocked || canLock) && <EditCardImageButton location="right" onClick={onClick} />}
        </>
      )}
      {!url && (
        <button className={styles['right-picture-edit']} onClick={onClick} disabled={isLocked && !canLock}>
          <FontAwesomeIcon icon={faCube} size="lg" />
        </button>
      )}
      {editMode && <PicturePreventOverrideIcon locked={isLocked} position="far-bottom-right" />}
    </motion.div>
  );
};

export default CardHeader;
